import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { GlobalBookingTripDates, TripType } from '@fcom/common';
import { isPresent } from '@fcom/core/utils/utils';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { ButtonMode, ButtonSize, ButtonTheme, DateLabels, DateRange, UiLabels } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';

import {
  AmAvailability,
  AmHolidayType,
  AmHolidayTypeDuration,
  AmLocation,
  AmSearchable,
} from '../../interfaces/am-api.interface';

@Component({
  selector: 'fin-am-dates-selector',
  templateUrl: 'am-dates-selector.component.html',
  styleUrls: ['./am-dates-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AmDatesSelectorComponent {
  private languageService = inject(LanguageService);

  readonly datepickerLabels$ = this.languageService.translate<UiLabels>('datepicker');
  readonly dateLabels$ = this.languageService.translate<DateLabels>('date');

  readonly AmHolidayType = AmHolidayType;
  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TripType = TripType;

  readonly disabled = input(false);

  readonly travelDates = input<GlobalBookingTripDates>();

  readonly amDestination = input<AmLocation>();

  readonly amAvailability = input<AmAvailability>();

  readonly setTravelDates = output<GlobalBookingTripDates>();

  readonly setDuration = output<AmHolidayTypeDuration>();

  modalOpen = false;
  readonly selectedDuration = signal<AmHolidayTypeDuration>(undefined);
  calendarRange: DateRange = [LocalDate.now(), LocalDate.now().plusDays(360)];

  readonly datePickerTitleLabel = computed(() => {
    const amDestination = this.amDestination();
    if (!isPresent(amDestination)) {
      return '';
    }
    if (amDestination.holidayType === AmHolidayType.AM) {
      return 'bookingWidget.priceCalendar.selectDeparture.oneWay';
    }
    return isPresent(this.travelDates()?.departureDate)
      ? 'bookingWidget.priceCalendar.selectReturn'
      : 'bookingWidget.priceCalendar.selectDeparture.roundTrip';
  });

  readonly disabledDateRanges = computed<DateRange[]>(() => {
    const amDestination = this.amDestination();
    if (!isPresent(amDestination)) {
      return [];
    }
    if (amDestination.holidayType === AmHolidayType.AM) {
      return this.calculateDisabledDatesBasedOnSearchables(amDestination.searchable);
    }
    const amAvailability = this.amAvailability();
    const travelDates = this.travelDates();
    if (!isPresent(amAvailability) || !isPresent(travelDates)) {
      return [];
    }
    return this.getDisabledDatesForDpHolidayType(amAvailability, travelDates);
  });

  readonly datesSelected = computed(() => {
    const travelDates = this.travelDates();
    return isPresent(travelDates?.departureDate) && isPresent(travelDates?.returnDate);
  });

  readonly suggestedAmTravelDays = computed<string[]>(() => {
    const availability = this.amAvailability();
    const duration = this.selectedDuration();
    if (!availability || !duration) {
      return [];
    }
    return Object.entries(availability)
      .filter((dayEntry) => !!dayEntry[1][duration.code])
      .map((dayEntry) => dayEntry[0]);
  });

  selectDuration(duration: AmHolidayTypeDuration): void {
    this.selectedDuration.set(duration);
    this.setDuration.emit(duration);
  }

  openModal(): void {
    this.modalOpen = true;
  }

  closeModal(): void {
    this.modalOpen = false;
  }

  updateCalendarDates(calendarDates: [string] | [string, string]): void {
    const [departureDate, returnDate] = calendarDates;
    const travelDates = this.travelDates();

    // The calendar emits (selectedDatesChange) on hover/render too, so only emit when values changed.
    if (departureDate !== travelDates?.departureDate?.id || returnDate !== travelDates?.returnDate?.id) {
      this.setTravelDates.emit({
        departureDate: departureDate ? new LocalDate(departureDate) : undefined,
        returnDate: returnDate ? new LocalDate(returnDate) : undefined,
      });
    }
  }

  private calculateDisabledDatesBasedOnSearchables(searchables: AmSearchable[]): DateRange[] {
    return [...searchables]
      .sort((a, b) => (a.from > b.from ? 1 : a.from === b.from ? 0 : -1))
      .reduce((disabledDateRanges: DateRange[], searchable, currentIndex, allSearchables) => {
        const returnValue = disabledDateRanges;

        // disable all days before first searchable's "from" date
        if (currentIndex === 0) {
          returnValue.push([LocalDate.now(), new LocalDate(searchable.from).minusDays(1)]);
        }

        // disable all days after last searchable's "to" date
        if (currentIndex === allSearchables.length - 1) {
          returnValue.push([new LocalDate(searchable.to).plusDays(1), new LocalDate(searchable.to).plusYears(1)]);
        } else {
          // disable days between current and next searchable if not last
          returnValue.push([
            new LocalDate(searchable.to).plusDays(1),
            new LocalDate(allSearchables[currentIndex + 1].from).minusDays(1),
          ]);
        }

        return returnValue;
      }, []);
  }

  private getDisabledDatesForDpHolidayType(
    amAvailability: AmAvailability,
    travelDates: GlobalBookingTripDates
  ): DateRange[] {
    if (travelDates.departureDate && !travelDates.returnDate) {
      return this.calculateDisabledDaysBasedOnDepartureAndAvailableDurations(amAvailability, travelDates.departureDate);
    }
    return this.calculateDisabledDaysBasedOnAvailableDays(Object.keys(amAvailability));
  }

  private calculateDisabledDaysBasedOnDepartureAndAvailableDurations(
    amAvailability: AmAvailability,
    departureDate: LocalDate
  ): DateRange[] {
    return Object.keys(amAvailability).length > 0
      ? this.calculateDisabledDaysBasedOnAvailableDays(
          Object.keys(amAvailability[departureDate.toISOString().substring(0, 10)] || {})
            .map((duration) => parseInt(duration, 10))
            .sort((a, b) => a - b)
            .reduce((dates: LocalDate[], duration) => [...dates, departureDate.plusDays(duration)], [departureDate])
            .map((date) => date.toISOString().substring(0, 10))
        )
      : [];
  }

  private calculateDisabledDaysBasedOnAvailableDays(days: string[]): DateRange[] {
    return [...days]
      .sort((a, b) => (a > b ? 1 : a === b ? 0 : -1))
      .reduce((disabledDateRanges: DateRange[], availableDay, currentIndex, availableDays) => {
        const returnValue = disabledDateRanges;

        if (currentIndex === 0) {
          // disable days before first available date
          returnValue.push([LocalDate.now(), new LocalDate(availableDay).minusDays(1)]);
        }

        // disable all days after last available day
        if (currentIndex === availableDays.length - 1) {
          returnValue.push([new LocalDate(availableDay).plusDays(1), new LocalDate(availableDay).plusYears(1)]);
        } else if (new LocalDate(availableDay).plusDays(1).lt(new LocalDate(availableDays[currentIndex + 1]))) {
          // disable days between current and next available day if not last
          returnValue.push([
            new LocalDate(availableDay).plusDays(1),
            new LocalDate(availableDays[currentIndex + 1]).minusDays(1),
          ]);
        }

        return returnValue;
      }, []);
  }
}
